// ============================================
// COMPONENT - Vista de Folios Activos
// ============================================

import { useState, useRef } from 'react';
import {
  useFoliosActivosSii,
  useUploadFolios,
  useUIStore,
  useSession,
  useCertificadoSiiStatus,
  useTimbrarFolios,
} from '@almacenes/core';
import { MdError, MdWarning, MdCheckCircle, MdUploadFile, MdBolt, MdInfo } from 'react-icons/md';
import { FaCrown } from 'react-icons/fa';
import { PremiumRequiredModal } from '@/components/PremiumRequiredModal';
import { TimbrarFoliosModal } from './TimbrarFoliosModal';

const TIPO_BOLETA = 39;

const CERT_AVISO_COLORS = {
  gray: { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-800', icon: 'text-gray-500' },
  red: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800', icon: 'text-red-500' },
  orange: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-800', icon: 'text-orange-500' },
} as const;

export const FoliosView = () => {
  const { data: folios, isLoading, error } = useFoliosActivosSii();
  const uploadFoliosMutation = useUploadFolios();
  const timbrarFoliosMutation = useTimbrarFolios();
  const { data: session } = useSession();
  const { data: certificado } = useCertificadoSiiStatus();
  const addToast = useUIStore((state) => state.addToast);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [showTimbrarModal, setShowTimbrarModal] = useState(false);
  const [timbrarErrorMessage, setTimbrarErrorMessage] = useState<string | null>(null);

  const hasPremiumAccess = session?.hasPremiumAccess || false;
  // Solo boleta electrónica (tipo 39): el scraper SII no soporta otros tipos de documento.
  const boletas39 = (folios ?? []).filter((f) => f.tipoDocumento === TIPO_BOLETA);
  const hayLoteVigente = boletas39.some((f) => f.diasRestantes > 0 && f.foliosDisponibles > 0);
  // La acción solo se ofrece cuando hace falta: sin folios del tipo, con pocos disponibles
  // o próximos a vencer (mismo criterio que la alerta visual de la tabla).
  const requiereCargaAutomatica =
    boletas39.length === 0 || boletas39.some((f) => f.alertaCritica || f.diasRestantes <= 30);

  // Si el certificado tiene un problema conocido, se bloquea el botón para evitar un intento
  // que sabemos que va a fallar, mostrando el motivo exacto en el aviso.
  const certificadoVigente =
    !!certificado?.hasCertificate && certificado.isValid && certificado.isPasswordCorrect;
  const timbrajeDeshabilitadoPorCertificado = hasPremiumAccess && !!certificado && !certificadoVigente;

  const getCertificadoAviso = () => {
    if (!requiereCargaAutomatica || !hasPremiumAccess || !certificado) return null;
    if (!certificado.hasCertificate) {
      return {
        color: 'gray' as const,
        Icon: MdInfo,
        message:
          'No tienes un certificado digital cargado. Regístralo en la pestaña "Certificado Digital" antes de usar la carga automática.',
      };
    }
    if (!certificado.isValid) {
      return {
        color: 'red' as const,
        Icon: MdError,
        message: 'Tu certificado digital está vencido. Actualízalo en la pestaña "Certificado Digital".',
      };
    }
    if (!certificado.isPasswordCorrect) {
      return {
        color: 'orange' as const,
        Icon: MdWarning,
        message:
          'La contraseña configurada del certificado digital es incorrecta. Corrígela en la pestaña "Certificado Digital" para poder usar la carga automática.',
      };
    }
    return null;
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleTimbrarClick = () => {
    if (!hasPremiumAccess) {
      setShowPremiumModal(true);
      return;
    }
    setTimbrarErrorMessage(null);
    setShowTimbrarModal(true);
  };

  const handleCloseTimbrarModal = () => {
    setShowTimbrarModal(false);
    setTimbrarErrorMessage(null);
  };

  const handleConfirmTimbraje = async (cantidadFolios: number, desactivarLotesVigentes: boolean) => {
    setTimbrarErrorMessage(null);
    try {
      const resultado = await timbrarFoliosMutation.mutateAsync({
        cantidadFolios,
        desactivarLotesVigentes,
      });
      addToast(
        'success',
        resultado.fromCache
          ? 'Ya se habían cargado folios hoy; se aplicó el lote correspondiente.'
          : `Folios cargados automáticamente: ${resultado.folioInicial} a ${resultado.folioFinal}.`
      );
      setShowTimbrarModal(false);
    } catch (error: any) {
      const message = error.message || 'No se pudo completar la carga automática de folios. Intenta nuevamente.';
      setTimbrarErrorMessage(message);
      addToast('error', message);
      console.error('Error en la carga automática de folios:', error);
    }
  };

  const renderTimbrarButton = () => {
    if (!requiereCargaAutomatica) return null;
    return (
      <button
        onClick={handleTimbrarClick}
        disabled={isUploading || timbrajeDeshabilitadoPorCertificado}
        title={timbrajeDeshabilitadoPorCertificado ? 'Revisa el aviso sobre tu certificado digital' : undefined}
        className="flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
      >
        <MdBolt className="text-xl" />
        <span>Carga Automática de Folios</span>
        {!hasPremiumAccess && (
          <span className="flex items-center gap-1 px-2 py-0.5 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full">
            <FaCrown className="text-xs text-white" />
            <span className="text-xs font-bold text-white">Premium</span>
          </span>
        )}
      </button>
    );
  };

  const renderCertificadoAviso = () => {
    const aviso = getCertificadoAviso();
    if (!aviso) return null;
    const colors = CERT_AVISO_COLORS[aviso.color];
    const { Icon } = aviso;
    return (
      <div className={`${colors.bg} border ${colors.border} rounded-lg p-4 flex items-start gap-3`}>
        <Icon className={`text-2xl flex-shrink-0 mt-0.5 ${colors.icon}`} />
        <p className={`text-sm ${colors.text}`}>{aviso.message}</p>
      </div>
    );
  };

  const renderModals = () => (
    <>
      <PremiumRequiredModal
        isOpen={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
        featureTitle="Carga automática de folios SII"
        featureDescription="El sistema descarga y carga automáticamente folios de boleta electrónica desde el SII, sin que tengas que ir al portal manualmente."
        benefits={[
          'Carga automática de folios en un clic, sin ir al portal del SII',
          'Los folios quedan listos de inmediato para emitir boletas',
          'Alertas de vencimiento y renovación asistida',
        ]}
      />
      <TimbrarFoliosModal
        isOpen={showTimbrarModal}
        onClose={handleCloseTimbrarModal}
        hayLoteVigente={hayLoteVigente}
        isLoading={timbrarFoliosMutation.isPending}
        errorMessage={timbrarErrorMessage}
        onConfirm={handleConfirmTimbraje}
      />
    </>
  );

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    
    if (!file) return;

    // Validar que sea un archivo XML
    if (!file.name.toLowerCase().endsWith('.xml')) {
      addToast('error', 'El archivo debe ser un XML');
      return;
    }

    setIsUploading(true);

    try {
      await uploadFoliosMutation.mutateAsync(file);
      addToast('success', 'Folios subidos exitosamente');
      
      // Limpiar el input para permitir subir el mismo archivo de nuevo
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error: any) {
      // Manejar error 409 (conflicto)
      if (error.statusCode === 409) {
        addToast('error', error.message || 'El rango de folios ya existe');
      } else {
        addToast('error', error.message || 'Error al subir los folios. Intenta nuevamente');
      }
      console.error('Error al subir folios:', error);
    } finally {
      setIsUploading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-600">Cargando folios activos...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <MdError className="text-red-500 text-2xl flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-red-800 font-semibold">Error al cargar folios</h3>
            <p className="text-red-600 text-sm mt-1">
              No se pudo obtener la información de los folios activos
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!folios || folios.length === 0) {
    return (
      <div className="space-y-6">
        {/* Input oculto para seleccionar archivo */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".xml"
          onChange={handleFileChange}
          className="hidden"
        />

        {/* Botones para subir o timbrar folios */}
        <div className="flex justify-end gap-3">
          {renderTimbrarButton()}
          <button
            onClick={handleUploadClick}
            disabled={isUploading}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {isUploading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Subiendo...</span>
              </>
            ) : (
              <>
                <MdUploadFile className="text-xl" />
                <span>Subir Folios XML</span>
              </>
            )}
          </button>
        </div>

        {renderCertificadoAviso()}

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-12">
          <div className="text-center">
            <MdWarning className="text-gray-400 text-6xl mx-auto mb-4" />
            <h3 className="text-gray-700 font-semibold text-lg mb-2">
              No hay folios activos
            </h3>
            <p className="text-gray-600 text-sm">
              No se encontraron folios vigentes para emisión de documentos tributarios
            </p>
          </div>
        </div>

        {renderModals()}
      </div>
    );
  }

  // Estadísticas generales
  const totalFolios = folios.reduce((sum, f) => sum + f.foliosTotales, 0);
  const totalDisponibles = folios.reduce((sum, f) => sum + f.foliosDisponibles, 0);
  const foliosConAlerta = folios.filter(f => f.alertaCritica).length;
  
  // Verificar si todos los folios tienen más del 80% de uso
  const todosFoliosSobreUmbral = folios.every(f => f.porcentajeUso > 80);

  return (
    <div className="space-y-6">
      {/* Input oculto para seleccionar archivo */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".xml"
        onChange={handleFileChange}
        className="hidden"
      />

      {/* Botones para subir o timbrar folios */}
      <div className="flex justify-end gap-3">
        {renderTimbrarButton()}
        <button
          onClick={handleUploadClick}
          disabled={isUploading}
          className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
        >
          {isUploading ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <span>Subiendo...</span>
            </>
          ) : (
            <>
              <MdUploadFile className="text-xl" />
              <span>Subir Folios XML</span>
            </>
          )}
        </button>
      </div>

      {renderCertificadoAviso()}

      {/* Resumen */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-blue-600 text-sm font-medium">Total de Folios</p>
          <p className="text-blue-900 text-3xl font-bold mt-1">
            {totalFolios.toLocaleString('es-CL')}
          </p>
        </div>
        
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-green-600 text-sm font-medium">Folios Disponibles</p>
          <p className="text-green-900 text-3xl font-bold mt-1">
            {totalDisponibles.toLocaleString('es-CL')}
          </p>
        </div>
        
        <div className={`${foliosConAlerta > 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'} border rounded-lg p-4`}>
          <p className={`${foliosConAlerta > 0 ? 'text-red-600' : 'text-gray-600'} text-sm font-medium`}>
            Lotes con Alerta
          </p>
          <p className={`${foliosConAlerta > 0 ? 'text-red-900' : 'text-gray-900'} text-3xl font-bold mt-1`}>
            {foliosConAlerta}
          </p>
        </div>
      </div>

      {/* Tabla de Folios */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <h4 className="text-lg font-semibold text-gray-900">Lotes de Folios Activos</h4>
          <p className="text-sm text-gray-600 mt-1">
            Detalle de todos los lotes de folios vigentes
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo Documento
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Folios
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Disponibles
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Uso
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vencimiento
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Días Restantes
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {folios.map((folio, index) => {
                const porcentajeUsado = folio.porcentajeUso;
                const alertaPorUso = porcentajeUsado >= 80;
                const alertaPorVencimiento = folio.diasRestantes <= 30;
                const tieneAlerta = folio.alertaCritica || alertaPorUso || alertaPorVencimiento;

                return (
                  <tr key={index} className={tieneAlerta ? 'bg-red-50' : 'hover:bg-gray-50'}>
                    {/* Estado */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      {tieneAlerta ? (
                        <div className="flex items-center gap-2">
                          <MdWarning className="text-red-500 text-xl" />
                          <span className="text-red-700 font-medium text-sm">Alerta</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <MdCheckCircle className="text-green-500 text-xl" />
                          <span className="text-green-700 font-medium text-sm">Normal</span>
                        </div>
                      )}
                    </td>

                    {/* Tipo Documento */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{folio.nombreDocumento}</p>
                        <p className="text-xs text-gray-500">Tipo {folio.tipoDocumento}</p>
                      </div>
                    </td>

                    {/* Total Folios */}
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <span className="text-sm font-semibold text-gray-900">
                        {folio.foliosTotales.toLocaleString('es-CL')}
                      </span>
                    </td>

                    {/* Disponibles */}
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <span className={`text-sm font-semibold ${
                        alertaPorUso ? 'text-red-700' : 'text-green-700'
                      }`}>
                        {folio.foliosDisponibles.toLocaleString('es-CL')}
                      </span>
                    </td>

                    {/* Porcentaje de Uso */}
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex flex-col items-end gap-1">
                        <span className={`text-sm font-semibold ${
                          porcentajeUsado >= 80 ? 'text-red-700' : 
                          porcentajeUsado >= 50 ? 'text-yellow-700' : 
                          'text-gray-700'
                        }`}>
                          {porcentajeUsado.toFixed(1)}%
                        </span>
                        <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${
                              porcentajeUsado >= 80 ? 'bg-red-500' : 
                              porcentajeUsado >= 50 ? 'bg-yellow-500' : 
                              'bg-green-500'
                            }`}
                            style={{ width: `${Math.min(porcentajeUsado, 100)}%` }}
                          />
                        </div>
                      </div>
                    </td>

                    {/* Fecha de Vencimiento */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {new Date(folio.fechaVencimiento + 'T00:00:00').toLocaleDateString('es-CL', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </span>
                    </td>

                    {/* Días Restantes */}
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <span className={`text-sm font-semibold ${
                        folio.diasRestantes <= 30 ? 'text-red-700' : 
                        folio.diasRestantes <= 60 ? 'text-yellow-700' : 
                        'text-gray-700'
                      }`}>
                        {folio.diasRestantes} días
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Alertas */}
      {todosFoliosSobreUmbral && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <MdWarning className="text-red-500 text-2xl flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-red-900 font-semibold mb-2">⚠️ Atención Requerida</h4>
              <ul className="text-red-800 text-sm space-y-2">
                {folios.filter(f => f.porcentajeUso > 80).map((folio, index) => (
                  <li key={index}>
                    • <strong>{folio.nombreDocumento}</strong>: {folio.porcentajeUso.toFixed(1)}% de folios utilizados
                    {folio.diasRestantes <= 30 && ` - Vence en ${folio.diasRestantes} días`}
                  </li>
                ))}
              </ul>
              <p className="text-red-700 text-sm mt-3">
                Todos los lotes de folios han superado el 80% de uso. Se recomienda solicitar nuevos lotes al SII para evitar interrupciones en la emisión de documentos.
              </p>
            </div>
          </div>
        </div>
      )}

      {renderModals()}
    </div>
  );
};
